<script setup>
import { ref, computed, onMounted, onActivated, nextTick, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { usePolicy } from 'dashboard/composables/usePolicy';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Modal from 'dashboard/components/Modal.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import {
  formatInReportingTimezone,
  zonedDateTimeToUtc,
} from 'dashboard/routes/dashboard/pipeline/utils/localeFormatting';
// NOOVI: CSV import (Gap FU-E — fase-11)
import FollowUpImportModal from './FollowUpImportModal.vue';
import {
  isHistoryFollowUpStatus,
  isPendingFollowUpStatus,
} from './followUpStatus';

const store = useStore();
const router = useRouter();
const { t, locale } = useI18n();
const { accountScopedRoute } = useAccount();
const { checkPermissions } = usePolicy();
const isAdmin = computed(() => checkPermissions(['administrator']));

const isLoading = ref(true);
const activeView = ref('pending'); // 'pending' | 'history'
const selectedInbox = ref('all');
const dateFilter = ref('all'); // 'all' | 'today' | 'week' | 'month'
const searchQuery = ref('');
const showSearchInput = ref(false);
const deleteConfirmationRef = ref(null);
const messageToDelete = ref(null);
const deletingIds = ref(new Set());
const retryingIds = ref(new Set());
const expandedConversations = ref(new Set());
const loadError = ref('');
const currentAccountId = computed(() => store.getters.getCurrentAccountId);
let accountGeneration = 0;

const currentAccount = computed(
  () =>
    store.getters['accounts/getAccount']?.(store.getters.getCurrentAccountId) ||
    store.getters.getCurrentAccount
);
const reportingTimezone = computed(
  () =>
    currentAccount.value?.reporting_timezone ||
    currentAccount.value?.settings?.reporting_timezone ||
    'UTC'
);
const localeForIntl = computed(() =>
  String(locale.value || 'en-US').replace('_', '-')
);

const shiftDateKey = (dateKey, days) => {
  const date = new Date(`${dateKey}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};
const reportingTodayKey = () =>
  formatInReportingTimezone(new Date(), 'yyyy-MM-dd', reportingTimezone.value);
const reportingDayStart = offset =>
  zonedDateTimeToUtc(
    shiftDateKey(reportingTodayKey(), offset),
    '00:00',
    reportingTimezone.value
  );

// NOOVI: CSV import state (Gap FU-E — fase-11)
const showImportModal = ref(false);
const onImported = () =>
  store.dispatch('followUps/fetchAll', {
    accountId: currentAccountId.value,
    generation: accountGeneration,
  });

// Edit state
const showEditModal = ref(false);
const messageToEdit = ref(null);
const isUpdating = ref(false);
const editForm = ref({
  title: '',
  message: '',
  scheduled_at: '',
  follow_up_template_id: '',
});

// Templates
const templates = computed(
  () => store.getters['followUpTemplates/getTemplates'] || []
);

const allFollowUps = computed(() => {
  return store.getters['followUps/getAllFollowUps'] || [];
});

// Unique inboxes from data
const availableInboxes = computed(() => {
  const inboxMap = new Map();
  allFollowUps.value.forEach(msg => {
    if (msg.inbox_id && msg.inbox_name) {
      inboxMap.set(msg.inbox_id, msg.inbox_name);
    }
  });
  return Array.from(inboxMap.entries()).map(([id, name]) => ({ id, name }));
});

const inboxOptions = computed(() => [
  { value: 'all', label: t('FOLLOW_UPS.FILTERS.ALL_INBOXES') },
  ...availableInboxes.value.map(inbox => ({
    value: inbox.id,
    label: inbox.name,
  })),
]);

const dateFilterOptions = computed(() => [
  { value: 'all', label: t('FOLLOW_UPS.FILTERS.DATE_ALL') },
  { value: 'today', label: t('FOLLOW_UPS.FILTERS.DATE_TODAY') },
  { value: 'week', label: t('FOLLOW_UPS.FILTERS.DATE_WEEK') },
  { value: 'month', label: t('FOLLOW_UPS.FILTERS.DATE_MONTH') },
]);

const templateOptions = computed(() => [
  { value: '', label: t('FOLLOW_UPS.FORM.NO_TEMPLATE') },
  ...templates.value.map(template => ({
    value: template.id,
    label: template.name,
  })),
]);

// Janela de data — a direção depende da aba, e é por isso que ela NÃO mora aqui.
//
// O filtro era um limite inferior único (`ts >= início de hoje/-7d/-30d`). Isso
// descreve o passado, então funcionava no Histórico e era inerte em Pendentes,
// onde quase tudo está agendado para o futuro: "Hoje" mantinha amanhã, semana
// que vem e mês que vem — igual a "Todos" —, e "7 dias" (7 dias ATRÁS) ficava
// mais permissivo que "Hoje", invertendo a ordem que os rótulos prometem.
// Relato de cliente 2026-08-11: "esses filtros aqui também não estão
// funcionando", com "Hoje" selecionado e "ESTA SEMANA (9)" ainda na tela.
//
// Pendentes olha para frente: a janela é um teto ("o que vence até…"). Sem piso,
// de propósito — um follow-up ATRASADO é o mais urgente que existe e precisa
// aparecer em qualquer janela (o grupo ATRASADOS em `pendingGrouped` depende
// disso). Histórico olha para trás e mantém o piso.
const DATE_WINDOW_DAYS = { today: 0, week: 7, month: 30 };

const applyDateWindow = (messages, direction) => {
  const days = DATE_WINDOW_DAYS[dateFilter.value];
  if (days === undefined) return messages;

  if (direction === 'future') {
    // Teto exclusivo: `today` vai até o fim de hoje (= início de amanhã).
    const limit = reportingDayStart(days + 1).getTime();
    return messages.filter(msg => (msg.scheduled_at || 0) * 1000 < limit);
  }

  const cutoff = reportingDayStart(-days).getTime();
  return messages.filter(
    msg => (msg.scheduled_at || msg.created_at) * 1000 >= cutoff
  );
};

// Base filter: inbox + search (a data entra por aba, ver applyDateWindow)
const baseFiltered = computed(() => {
  let messages = allFollowUps.value;

  // Inbox filter
  if (selectedInbox.value !== 'all') {
    const inboxId = parseInt(selectedInbox.value, 10);
    messages = messages.filter(msg => msg.inbox_id === inboxId);
  }

  // Search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase();
    messages = messages.filter(
      msg =>
        msg.title?.toLowerCase().includes(query) ||
        msg.message?.toLowerCase().includes(query) ||
        msg.conversation_display?.toLowerCase().includes(query) ||
        msg.inbox_name?.toLowerCase().includes(query)
    );
  }

  return messages;
});

// Pending: only pending status, sorted chronologically
const pendingMessages = computed(() => {
  return applyDateWindow(
    baseFiltered.value.filter(msg => isPendingFollowUpStatus(msg.status)),
    'future'
  ).sort((a, b) => a.scheduled_at - b.scheduled_at);
});

// Pending grouped by time period (Hoje / Esta Semana / Mais Tarde)
const pendingGrouped = computed(() => {
  const startOfToday = reportingDayStart(0);
  const startOfTomorrow = reportingDayStart(1);
  const startOfNextWeek = reportingDayStart(7);

  const groups = {
    overdue: {
      label: t('FOLLOW_UPS.GROUPS.OVERDUE'),
      items: [],
      key: 'overdue',
    },
    today: {
      label: t('FOLLOW_UPS.GROUPS.TODAY'),
      items: [],
      key: 'today',
    },
    week: {
      label: t('FOLLOW_UPS.GROUPS.THIS_WEEK'),
      items: [],
      key: 'week',
    },
    later: {
      label: t('FOLLOW_UPS.GROUPS.LATER'),
      items: [],
      key: 'later',
    },
  };

  pendingMessages.value.forEach(msg => {
    const scheduledDate = new Date(msg.scheduled_at * 1000);
    if (scheduledDate < startOfToday) {
      groups.overdue.items.push(msg);
    } else if (scheduledDate < startOfTomorrow) {
      groups.today.items.push(msg);
    } else if (scheduledDate < startOfNextWeek) {
      groups.week.items.push(msg);
    } else {
      groups.later.items.push(msg);
    }
  });

  return Object.values(groups).filter(g => g.items.length > 0);
});

// History: sent + failed + cancelled, grouped by conversation
const historyMessages = computed(() => {
  return applyDateWindow(
    baseFiltered.value.filter(msg => isHistoryFollowUpStatus(msg.status)),
    'past'
  ).sort((a, b) => b.scheduled_at - a.scheduled_at);
});

const historyGrouped = computed(() => {
  const groups = new Map();
  historyMessages.value.forEach(msg => {
    const key = msg.conversation_display || `#${msg.conversation_id}`;
    if (!groups.has(key)) {
      groups.set(key, {
        label: key,
        conversation_id: msg.conversation_id,
        inbox_name: msg.inbox_name,
        items: [],
      });
    }
    groups.get(key).items.push(msg);
  });
  return Array.from(groups.values());
});

const hasActiveFilters = computed(
  () =>
    dateFilter.value !== 'all' ||
    selectedInbox.value !== 'all' ||
    Boolean(searchQuery.value)
);

// Limpa os TRÊS filtros. O empty state só aparece quando algum está ativo, e
// zerar apenas a data deixaria a tela vazia com o botão sumido — sem saída
// visível para o usuário.
const clearFilters = () => {
  dateFilter.value = 'all';
  selectedInbox.value = 'all';
  searchQuery.value = '';
  showSearchInput.value = false;
};

// O badge da aba conta o TOTAL — é com ele que se navega ("tenho 82 pendentes").
// Sob filtro ele passa a mostrar "12/82": mostrar só o total contradizia a lista
// ("Pendentes 82" sobre 3 cards) e reforçava a leitura de que o filtro não fez
// nada; mostrar só o filtrado apagaria o total. Os números filtrados saem das
// mesmas listas renderizadas, então badge e tela não podem divergir.
const statusCounts = computed(() => ({
  pending: allFollowUps.value.filter(m => isPendingFollowUpStatus(m.status))
    .length,
  history: allFollowUps.value.filter(m => isHistoryFollowUpStatus(m.status))
    .length,
  filteredPending: pendingMessages.value.length,
  filteredHistory: historyMessages.value.length,
}));

const tabBadge = (filtered, total) =>
  hasActiveFilters.value && filtered !== total ? `${filtered}/${total}` : total;

const isCurrentFollowUpContext = (accountId, generation) =>
  String(accountId) === String(currentAccountId.value) &&
  generation === accountGeneration;

const resetFollowUpsView = () => {
  accountGeneration += 1;
  if (deleteConfirmationRef.value?.show) {
    deleteConfirmationRef.value.cancel();
  }
  store.commit('followUps/RESET_ACCOUNT_DATA');
  store.commit('followUpTemplates/RESET_ACCOUNT_DATA');
  activeView.value = 'pending';
  selectedInbox.value = 'all';
  dateFilter.value = 'all';
  searchQuery.value = '';
  showSearchInput.value = false;
  messageToDelete.value = null;
  deletingIds.value = new Set();
  retryingIds.value = new Set();
  expandedConversations.value = new Set();
  showImportModal.value = false;
  showEditModal.value = false;
  messageToEdit.value = null;
  isUpdating.value = false;
  editForm.value = {
    title: '',
    message: '',
    scheduled_at: '',
    follow_up_template_id: '',
  };
  loadError.value = '';
  isLoading.value = true;
};

const loadFollowUps = async ({ accountId, generation } = {}) => {
  const requestAccountId = accountId ?? currentAccountId.value;
  const requestGeneration = generation ?? accountGeneration;
  isLoading.value = true;
  loadError.value = '';
  try {
    await Promise.all([
      store.dispatch('followUps/fetchAll', {
        accountId: requestAccountId,
        generation: requestGeneration,
      }),
      store.dispatch('followUpTemplates/fetch', {
        accountId: requestAccountId,
        generation: requestGeneration,
      }),
    ]);
  } catch {
    if (isCurrentFollowUpContext(requestAccountId, requestGeneration)) {
      loadError.value = t('FOLLOW_UPS.MESSAGES.LOAD_ERROR');
    }
  } finally {
    if (isCurrentFollowUpContext(requestAccountId, requestGeneration)) {
      isLoading.value = false;
    }
  }
};

onMounted(() => {
  accountGeneration += 1;
  loadFollowUps({
    accountId: currentAccountId.value,
    generation: accountGeneration,
  });
});

watch(
  currentAccountId,
  (accountId, previousAccountId) => {
    if (String(accountId) === String(previousAccountId)) return;
    resetFollowUpsView();
    loadFollowUps({ accountId, generation: accountGeneration });
  },
  { flush: 'sync' }
);

// BUG-FU-02: when this view is kept-alive and the user returns to it after
// cancelling/creating a follow-up from a conversation, re-fetch so the Pending/
// History counts and lists are fresh. Skip the first activation (it coincides
// with onMounted) to avoid a duplicate initial fetch.
const isFirstActivation = ref(true);
onActivated(() => {
  if (isFirstActivation.value) {
    isFirstActivation.value = false;
    return;
  }
  resetFollowUpsView();
  loadFollowUps({
    accountId: currentAccountId.value,
    generation: accountGeneration,
  });
});

// Search
const handleSearch = () => {
  showSearchInput.value = !showSearchInput.value;
  if (showSearchInput.value) {
    nextTick(() => {
      document.getElementById('follow-ups-search')?.focus();
    });
  } else {
    searchQuery.value = '';
  }
};

// Navigation
const goToTemplates = () =>
  router.push(accountScopedRoute('follow_up_templates'));
const goToAutomations = () =>
  router.push(accountScopedRoute('follow_up_automations'));

const goToConversation = conversationId => {
  if (!conversationId) return;
  router.push({
    name: 'inbox_conversation',
    params: { conversation_id: conversationId },
  });
};

const toggleConversation = key => {
  if (expandedConversations.value.has(key)) {
    expandedConversations.value.delete(key);
  } else {
    expandedConversations.value.add(key);
  }
};

const isExpanded = key => expandedConversations.value.has(key);

// Template — manual follow-up quick apply.
// BUG-FU05 (audit 2026-04-25): previously used template.content which,
// because of BUG-FU01, was the template NAME, not the message. After
// FU01 is fixed, content is empty for item-based templates; we now
// fetch items and concatenate their text content. Attachments are
// ignored at this stage — users can attach via the sequence builder
// if they need rich messages.
const applyTemplate = async templateId => {
  const template = templates.value.find(item => item.id === templateId);
  if (!template) return;

  editForm.value.title = editForm.value.title || template.name;

  if (template.uses_items) {
    try {
      const items = await store.dispatch(
        'followUpTemplates/fetchItems',
        templateId
      );
      const textParts = (items || [])
        .filter(i => i.item_type === 'text' && i.content)
        .map(i => i.content);
      editForm.value.message = textParts.join('\n\n') || '';
    } catch {
      useAlert(t('FOLLOW_UPS.MESSAGES.LOAD_ERROR'));
    }
  } else {
    editForm.value.message = template.content || '';
  }
};

const formatDate = timestamp => {
  const date = new Date(timestamp * 1000);
  return new Intl.DateTimeFormat(localeForIntl.value, {
    dateStyle: 'short',
    timeStyle: 'short',
    timeZone: reportingTimezone.value,
  }).format(date);
};

const formatRelativeTime = timestamp => {
  const now = Date.now();
  const diff = timestamp * 1000 - now;
  const absDiff = Math.abs(diff);
  let unit = 'minute';
  let divisor = 60000;
  if (absDiff >= 86400000) {
    unit = 'day';
    divisor = 86400000;
  } else if (absDiff >= 3600000) {
    unit = 'hour';
    divisor = 3600000;
  }

  return new Intl.RelativeTimeFormat(localeForIntl.value, {
    numeric: 'always',
  }).format(Math.round(diff / divisor), unit);
};

const getStatusClass = status => {
  switch (status) {
    case 'pending':
    case 'scheduled':
      return 'bg-n-amber-3 text-n-amber-11 border border-n-amber-6';
    case 'sent':
      return 'bg-n-teal-3 text-n-teal-11 border border-n-teal-6';
    case 'failed':
      return 'bg-n-ruby-3 text-n-ruby-11 border border-n-ruby-6';
    case 'cancelled':
      return 'bg-n-alpha-1 text-n-slate-11 border border-n-weak';
    default:
      return 'bg-n-alpha-1 text-n-slate-12 border border-n-weak';
  }
};

const getStatusLabel = status => {
  const labels = {
    pending: t('FOLLOW_UPS.STATUS.PENDING'),
    scheduled: t('FOLLOW_UPS.STATUS.SCHEDULED'),
    sent: t('FOLLOW_UPS.STATUS.SENT'),
    failed: t('FOLLOW_UPS.STATUS.FAILED'),
    cancelled: t('FOLLOW_UPS.STATUS.CANCELLED'),
  };
  return labels[status] || status;
};

const getStatusIcon = status => {
  switch (status) {
    case 'sent':
      return 'i-lucide-check-circle-2';
    case 'failed':
      return 'i-lucide-x-circle';
    case 'cancelled':
      return 'i-lucide-ban';
    default:
      return 'i-lucide-clock';
  }
};

const isOverdue = msg => msg.scheduled_at * 1000 < Date.now();
const isPendingFollowUp = message => isPendingFollowUpStatus(message?.status);

const deleteMessage = async message => {
  if (!isPendingFollowUp(message)) return;
  deletingIds.value.add(message.id);
  try {
    await store.dispatch('followUps/delete', {
      conversationId: message.conversation_id,
      id: message.id,
    });
    useAlert(t('FOLLOW_UPS.MESSAGES.DELETE_SUCCESS'));
  } catch (error) {
    // BUG-03 (audit 2026-06-16): only PENDING follow-ups are deletable; surface a
    // clear reason if one already transitioned (sent/failed/cancelled).
    useAlert(
      error?.response?.status === 422
        ? t('FOLLOW_UPS.MESSAGES.DELETE_NOT_PENDING')
        : t('FOLLOW_UPS.MESSAGES.ERROR')
    );
  } finally {
    deletingIds.value.delete(message.id);
  }
};

const confirmDelete = async message => {
  if (!isPendingFollowUp(message)) return;
  messageToDelete.value = message;
  const confirmed = await deleteConfirmationRef.value.showConfirmation();
  if (confirmed) {
    await deleteMessage(message);
  }
  messageToDelete.value = null;
};

const isDeleting = messageId => deletingIds.value.has(messageId);

const isRetrying = messageId => retryingIds.value.has(messageId);

// Re-send a failed follow-up. The backend resumes a partially-delivered
// multi-item chain (audit A1), so the customer never receives an already-sent
// item again. conversation_id is the conversation display_id (serializer).
const handleRetry = async message => {
  retryingIds.value.add(message.id);
  try {
    await store.dispatch('followUps/retrySend', {
      conversationId: message.conversation_id,
      id: message.id,
    });
    useAlert(t('FOLLOW_UPS.MESSAGES.RETRY_SUCCESS'));
  } catch (error) {
    useAlert(t('FOLLOW_UPS.MESSAGES.RETRY_ERROR'));
  } finally {
    retryingIds.value.delete(message.id);
  }
};

const openEditModal = message => {
  if (!isPendingFollowUp(message)) return;
  messageToEdit.value = message;
  const localDateTime = formatInReportingTimezone(
    message.scheduled_at * 1000,
    "yyyy-MM-dd'T'HH:mm",
    reportingTimezone.value
  );
  editForm.value = {
    title: message.title || '',
    message: message.message || '',
    scheduled_at: localDateTime,
    follow_up_template_id: '',
  };
  showEditModal.value = true;
};

const closeEditModal = () => {
  if (isUpdating.value) return;
  showEditModal.value = false;
  messageToEdit.value = null;
  editForm.value = {
    title: '',
    message: '',
    scheduled_at: '',
    follow_up_template_id: '',
  };
};

const handleEditSubmit = async () => {
  if (
    !messageToEdit.value ||
    !editForm.value.message.trim() ||
    !editForm.value.scheduled_at
  ) {
    useAlert(t('FOLLOW_UPS.MESSAGES.INVALID_FORM'));
    return;
  }
  isUpdating.value = true;
  let updateSucceeded = false;
  try {
    const [date, time] = editForm.value.scheduled_at.split('T');
    const scheduledAt = zonedDateTimeToUtc(date, time, reportingTimezone.value);
    if (!scheduledAt) throw new Error('invalid-follow-up-date');
    await store.dispatch('followUps/update', {
      conversationId: messageToEdit.value.conversation_id,
      id: messageToEdit.value.id,
      messageData: {
        title: editForm.value.title,
        content: editForm.value.message,
        scheduled_at: scheduledAt.toISOString(),
      },
    });
    await store.dispatch('followUps/fetchAll');
    useAlert(t('FOLLOW_UPS.MESSAGES.UPDATE_SUCCESS'));
    updateSucceeded = true;
  } catch (error) {
    useAlert(t('FOLLOW_UPS.MESSAGES.ERROR'));
  } finally {
    isUpdating.value = false;
    if (updateSucceeded) closeEditModal();
  }
};
</script>

<template>
  <div class="flex flex-col h-full w-full bg-n-background">
    <!-- Header -->
    <header
      class="relative z-50 flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-n-weak bg-n-background"
    >
      <!-- Left: Icon chip + Title + subtitle -->
      <div class="flex items-center gap-3 min-w-0">
        <div
          class="flex size-9 flex-shrink-0 items-center justify-center rounded-xl bg-n-brand/10 text-n-brand"
        >
          <span class="i-lucide-bell-ring size-4" aria-hidden="true" />
        </div>
        <div class="min-w-0">
          <h1
            class="text-sm font-semibold text-n-slate-12 truncate leading-tight"
          >
            {{ t('FOLLOW_UPS.PAGE.TITLE') }}
          </h1>
          <p
            class="hidden md:block text-xs text-n-slate-10 truncate leading-tight"
          >
            {{ t('FOLLOW_UPS.PAGE.DESCRIPTION') }}
          </p>
        </div>
      </div>

      <!-- Right: Nav buttons (admin only) -->
      <div v-if="isAdmin" class="flex items-center gap-1.5">
        <button
          type="button"
          class="flex min-h-11 items-center gap-1.5 px-3 rounded-lg text-xs font-medium border border-n-weak bg-n-background text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
          @click="goToTemplates"
        >
          <span class="i-lucide-file-text size-3.5" />
          {{ t('FOLLOW_UPS.NAV.TEMPLATES') }}
        </button>
        <button
          type="button"
          class="flex min-h-11 items-center gap-1.5 px-3 rounded-lg text-xs font-medium border border-n-weak bg-n-background text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
          @click="goToAutomations"
        >
          <span class="i-lucide-zap size-3.5" />
          {{ t('FOLLOW_UPS.NAV.AUTOMATIONS') }}
        </button>
        <!-- NOOVI: CSV import (Gap FU-E — fase-11) -->
        <button
          type="button"
          class="flex min-h-11 items-center gap-1.5 px-3 rounded-lg text-xs font-medium border border-n-weak bg-n-background text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
          @click="showImportModal = true"
        >
          <span class="i-lucide-upload size-3.5" />
          {{ t('FOLLOW_UPS.IMPORT.BUTTON') }}
        </button>
      </div>
    </header>

    <!-- NOOVI: modal de import CSV (Gap FU-E — fase-11) -->
    <FollowUpImportModal
      v-model:show="showImportModal"
      @imported="onImported"
    />

    <!-- Toolbar: Pill-switcher + Filters (single compact row) -->
    <div
      class="flex min-h-14 flex-shrink-0 flex-wrap items-center gap-2 px-3 py-1.5 border-b border-n-weak bg-n-background"
    >
      <!-- Pill-style view switcher -->
      <nav
        :aria-label="t('FOLLOW_UPS.PAGE.FILTER_BY_STATUS')"
        class="flex items-center rounded-lg bg-n-alpha-1 p-0.5 flex-shrink-0"
      >
        <button
          type="button"
          class="flex min-h-11 items-center gap-1.5 px-2.5 py-2 text-[11px] font-medium rounded-md focus-visible:outline-2 focus-visible:outline-n-brand transition-colors duration-150"
          :aria-pressed="activeView === 'pending'"
          :class="
            activeView === 'pending'
              ? 'bg-n-solid-2 shadow-sm text-n-slate-12'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="activeView = 'pending'"
        >
          <span class="i-lucide-clock size-3" />
          {{ t('FOLLOW_UPS.VIEWS.PENDING') }}
          <span
            v-if="statusCounts.pending > 0"
            class="inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-semibold rounded-full"
            :class="
              activeView === 'pending'
                ? 'bg-n-brand text-white'
                : 'bg-n-alpha-2 text-n-slate-10'
            "
          >
            {{ tabBadge(statusCounts.filteredPending, statusCounts.pending) }}
          </span>
        </button>
        <button
          type="button"
          class="flex min-h-11 items-center gap-1.5 px-2.5 py-2 text-[11px] font-medium rounded-md focus-visible:outline-2 focus-visible:outline-n-brand transition-colors duration-150"
          :aria-pressed="activeView === 'history'"
          :class="
            activeView === 'history'
              ? 'bg-n-solid-2 shadow-sm text-n-slate-12'
              : 'text-n-slate-10 hover:text-n-slate-12'
          "
          @click="activeView = 'history'"
        >
          <span class="i-lucide-history size-3" />
          {{ t('FOLLOW_UPS.VIEWS.HISTORY') }}
          <span
            v-if="statusCounts.history > 0"
            class="inline-flex items-center justify-center min-w-[16px] h-4 px-1 text-[10px] font-semibold rounded-full"
            :class="
              activeView === 'history'
                ? 'bg-n-brand text-white'
                : 'bg-n-alpha-2 text-n-slate-10'
            "
          >
            {{ tabBadge(statusCounts.filteredHistory, statusCounts.history) }}
          </span>
        </button>
      </nav>

      <div class="w-px h-4 bg-n-weak flex-shrink-0" />

      <!-- Expandable Search -->
      <div class="flex items-center flex-shrink-0">
        <div
          class="flex items-center overflow-hidden transition-[width] duration-200 ease-out motion-reduce:transition-none"
          :class="showSearchInput ? 'w-52' : 'w-0'"
        >
          <div class="relative w-full">
            <span
              class="pointer-events-none absolute left-3 top-1/2 z-10 size-3.5 -translate-y-1/2 i-lucide-search text-n-slate-9"
            />
            <Input
              id="follow-ups-search"
              v-model="searchQuery"
              type="search"
              custom-input-class="!h-11 !pl-9 text-[11px]"
              :placeholder="t('FOLLOW_UPS.PAGE.SEARCH_PLACEHOLDER')"
              :aria-label="t('FOLLOW_UPS.PAGE.SEARCH_PLACEHOLDER')"
              @blur="!searchQuery && (showSearchInput = false)"
            />
          </div>
        </div>
        <NextButton
          variant="ghost"
          color="slate"
          size="xs"
          :aria-label="t('FOLLOW_UPS.PAGE.SEARCH_PLACEHOLDER')"
          class="!size-11"
          @click="handleSearch"
        >
          <template #icon>
            <span class="i-lucide-search size-3.5" />
          </template>
        </NextButton>
      </div>

      <!-- Inbox Filter -->
      <Select
        v-if="availableInboxes.length > 0"
        v-model="selectedInbox"
        class="max-w-[220px] flex-shrink-0"
        :options="inboxOptions"
        :aria-label="t('FOLLOW_UPS.FILTERS.ALL_INBOXES')"
      />

      <!-- Date Filter pills -->
      <div class="flex items-center gap-0.5 flex-shrink-0">
        <button
          v-for="filter in dateFilterOptions"
          :key="filter.value"
          type="button"
          class="min-h-11 px-2.5 py-2 text-[11px] font-medium rounded-md focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
          :aria-pressed="dateFilter === filter.value"
          :class="
            dateFilter === filter.value
              ? 'bg-n-brand text-white'
              : 'text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1'
          "
          @click="dateFilter = filter.value"
        >
          {{ filter.label }}
        </button>
      </div>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto">
      <!-- Loading -->
      <div
        v-if="isLoading"
        role="status"
        aria-live="polite"
        class="flex flex-col items-center justify-center min-h-[400px]"
      >
        <div
          class="w-8 h-8 border-2 border-n-weak border-t-n-brand rounded-full animate-spin motion-reduce:animate-none mb-3"
        />
        <p class="text-[11px] text-n-slate-10">
          {{ t('FOLLOW_UPS.PAGE.LOADING') }}
        </p>
      </div>

      <div
        v-else-if="loadError"
        role="alert"
        class="flex min-h-[360px] flex-col items-center justify-center gap-4 p-6 text-center"
      >
        <span class="i-lucide-alert-triangle size-8 text-n-ruby-10" />
        <p class="max-w-md text-sm text-n-ruby-11">{{ loadError }}</p>
        <NextButton
          variant="outline"
          color="slate"
          size="md"
          @click="loadFollowUps"
        >
          {{ t('FOLLOW_UPS.PAGE.RETRY') }}
        </NextButton>
      </div>

      <!-- PENDING VIEW -->
      <div v-else-if="activeView === 'pending'" class="p-4">
        <!-- Empty pending -->
        <div
          v-if="pendingGrouped.length === 0"
          class="flex flex-col items-center justify-center min-h-[360px] text-center"
        >
          <div
            class="flex size-16 items-center justify-center rounded-2xl mb-4"
            :class="
              hasActiveFilters
                ? 'bg-n-alpha-2 text-n-slate-11'
                : 'bg-n-teal-3 text-n-teal-11'
            "
          >
            <span
              class="size-8"
              :class="
                hasActiveFilters
                  ? 'i-lucide-filter-x'
                  : 'i-lucide-check-circle-2'
              "
            />
          </div>
          <h3 class="text-base font-semibold text-n-slate-12 mb-1">
            {{
              hasActiveFilters
                ? t('FOLLOW_UPS.VIEWS.FILTERED_EMPTY_TITLE')
                : t('FOLLOW_UPS.VIEWS.PENDING_EMPTY_TITLE')
            }}
          </h3>
          <p class="text-sm text-n-slate-10 max-w-sm">
            {{
              hasActiveFilters
                ? t('FOLLOW_UPS.VIEWS.FILTERED_EMPTY_DESC')
                : t('FOLLOW_UPS.VIEWS.PENDING_EMPTY_DESC')
            }}
          </p>
          <NextButton
            v-if="hasActiveFilters"
            variant="ghost"
            color="slate"
            size="sm"
            class="mt-3"
            @click="clearFilters"
          >
            {{ t('FOLLOW_UPS.FILTERS.CLEAR') }}
          </NextButton>
        </div>

        <!-- Grouped pending -->
        <div v-else class="space-y-5">
          <div v-for="group in pendingGrouped" :key="group.key">
            <!-- Group Header -->
            <div class="flex items-center gap-2 mb-2.5">
              <span
                class="size-3.5 flex-shrink-0"
                :class="{
                  'i-lucide-alert-circle text-n-ruby-10':
                    group.key === 'overdue',
                  'i-lucide-sun text-n-amber-10': group.key === 'today',
                  'i-lucide-calendar text-n-slate-10': group.key === 'week',
                  'i-lucide-calendar-clock text-n-slate-10':
                    group.key === 'later',
                }"
              />
              <h2
                class="text-[10px] font-semibold text-n-slate-10 uppercase tracking-wider"
              >
                {{ group.label }}
              </h2>
              <span class="text-[10px] text-n-slate-9">
                {{
                  t('FOLLOW_UPS.VIEWS.GROUP_COUNT', {
                    count: group.items.length,
                  })
                }}
              </span>
              <div class="flex-1 h-px bg-n-weak" />
            </div>

            <!-- Cards grid -->
            <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
              <div
                v-for="message in group.items"
                :key="message.id"
                class="group rounded-xl outline outline-1 bg-n-solid-2 transition-colors duration-150 overflow-hidden"
                :class="
                  isOverdue(message)
                    ? 'outline-n-ruby-6 bg-n-ruby-2'
                    : 'outline-n-weak hover:outline-n-strong'
                "
              >
                <div class="p-3">
                  <!-- Title + overdue badge -->
                  <div class="flex items-start justify-between gap-2 mb-2">
                    <h3
                      class="text-[11px] font-semibold text-n-slate-12 leading-tight flex-1 min-w-0"
                    >
                      {{ message.title || t('FOLLOW_UPS.LIST.NO_TITLE') }}
                    </h3>
                    <span
                      v-if="isOverdue(message)"
                      class="flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-n-ruby-3 text-n-ruby-11 border border-n-ruby-6 uppercase tracking-wide"
                    >
                      {{ t('FOLLOW_UPS.VIEWS.OVERDUE') }}
                    </span>
                  </div>

                  <!-- Message preview -->
                  <p class="text-[11px] text-n-slate-11 line-clamp-2 mb-2.5">
                    {{ message.message }}
                  </p>

                  <!-- Meta -->
                  <div class="space-y-1">
                    <div
                      class="flex items-center gap-1.5 text-[10px] text-n-slate-10"
                    >
                      <span class="i-lucide-clock size-2.5 flex-shrink-0" />
                      <span>{{ formatDate(message.scheduled_at) }}</span>
                      <span class="text-n-weak" aria-hidden="true">
                        {{ t('FOLLOW_UPS.VIEWS.META_SEPARATOR') }}
                      </span>
                      <span
                        class="font-medium"
                        :class="
                          isOverdue(message) ? 'text-n-ruby-11' : 'text-n-brand'
                        "
                      >
                        {{ formatRelativeTime(message.scheduled_at) }}
                      </span>
                    </div>
                    <button
                      v-if="message.conversation_id"
                      type="button"
                      class="flex min-h-11 w-full items-center gap-1.5 rounded text-left text-[10px] text-n-slate-10 hover:text-n-brand focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
                      @click="goToConversation(message.conversation_id)"
                    >
                      <span
                        class="i-lucide-message-circle size-2.5 flex-shrink-0"
                      />
                      <span class="truncate font-medium">{{
                        message.conversation_display
                      }}</span>
                      <span
                        class="i-lucide-external-link size-2.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                      />
                    </button>
                    <div
                      class="flex items-center gap-1.5 text-[10px] text-n-slate-10"
                    >
                      <span class="i-lucide-inbox size-2.5 flex-shrink-0" />
                      <span class="truncate">{{ message.inbox_name }}</span>
                    </div>
                  </div>
                </div>

                <!-- Actions footer -->
                <div
                  class="px-2.5 py-1.5 border-t border-n-weak bg-n-background flex gap-1"
                >
                  <button
                    v-if="message.conversation_id"
                    type="button"
                    class="flex size-11 items-center justify-center rounded-md text-n-brand hover:bg-n-alpha-1 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
                    :title="t('FOLLOW_UPS.LIST.VIEW_CONVERSATION')"
                    @click="goToConversation(message.conversation_id)"
                  >
                    <span class="i-lucide-external-link size-3" />
                  </button>
                  <button
                    v-if="isPendingFollowUp(message)"
                    type="button"
                    class="flex min-h-11 flex-1 items-center justify-center gap-1 rounded-md text-[10px] font-medium text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-1 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
                    @click="openEditModal(message)"
                  >
                    <span class="i-lucide-edit-2 size-3" />
                    {{ t('FOLLOW_UPS.LIST.EDIT') }}
                  </button>
                  <button
                    v-if="isPendingFollowUp(message)"
                    type="button"
                    class="flex min-h-11 flex-1 items-center justify-center gap-1 rounded-md text-[10px] font-medium text-n-ruby-11 hover:bg-n-ruby-3 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
                    :disabled="isDeleting(message.id)"
                    @click="confirmDelete(message)"
                  >
                    <span
                      v-if="isDeleting(message.id)"
                      class="i-lucide-loader-2 size-3 animate-spin motion-reduce:animate-none"
                    />
                    <span v-else class="i-lucide-trash-2 size-3" />
                    {{
                      isDeleting(message.id)
                        ? t('FOLLOW_UPS.LIST.DELETING')
                        : t('FOLLOW_UPS.LIST.DELETE')
                    }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- HISTORY VIEW -->
      <div v-else-if="activeView === 'history'" class="p-4">
        <!-- Empty history -->
        <div
          v-if="historyGrouped.length === 0"
          class="flex flex-col items-center justify-center min-h-[360px] text-center"
        >
          <div
            class="w-16 h-16 rounded-full bg-n-alpha-1 flex items-center justify-center mb-4"
          >
            <span
              class="size-8 text-n-slate-10"
              :class="
                hasActiveFilters ? 'i-lucide-filter-x' : 'i-lucide-history'
              "
            />
          </div>
          <h3 class="text-sm font-semibold text-n-slate-12 mb-1">
            {{
              hasActiveFilters
                ? t('FOLLOW_UPS.VIEWS.FILTERED_EMPTY_TITLE')
                : t('FOLLOW_UPS.VIEWS.HISTORY_EMPTY_TITLE')
            }}
          </h3>
          <p class="text-[11px] text-n-slate-10 max-w-xs">
            {{
              hasActiveFilters
                ? t('FOLLOW_UPS.VIEWS.FILTERED_EMPTY_DESC')
                : t('FOLLOW_UPS.VIEWS.HISTORY_EMPTY_DESC')
            }}
          </p>
          <NextButton
            v-if="hasActiveFilters"
            variant="ghost"
            color="slate"
            size="sm"
            class="mt-3"
            @click="clearFilters"
          >
            {{ t('FOLLOW_UPS.FILTERS.CLEAR') }}
          </NextButton>
        </div>

        <!-- Grouped by conversation -->
        <div v-else class="space-y-1.5">
          <div
            v-for="group in historyGrouped"
            :key="group.label"
            class="border border-n-weak rounded-xl overflow-hidden bg-n-solid-2"
          >
            <!-- Conversation Group Header -->
            <div class="flex items-center hover:bg-n-alpha-1">
              <button
                type="button"
                class="flex min-h-11 min-w-0 flex-1 items-center gap-2.5 px-3 py-2.5 text-left focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
                :aria-expanded="isExpanded(group.label)"
                @click="toggleConversation(group.label)"
              >
                <span
                  class="w-7 h-7 rounded-full bg-n-alpha-1 flex items-center justify-center flex-shrink-0"
                >
                  <span class="i-lucide-user size-3.5 text-n-brand" />
                </span>
                <span class="flex-1 min-w-0">
                  <span
                    class="block text-[11px] font-semibold text-n-slate-12 truncate"
                  >
                    {{ group.label }}
                  </span>
                  <span class="block text-[10px] text-n-slate-10 truncate">
                    {{
                      t('FOLLOW_UPS.VIEWS.CONVERSATION_SUMMARY', {
                        inbox: group.inbox_name,
                        count: group.items.length,
                      })
                    }}
                  </span>
                </span>
                <span class="flex items-center gap-1.5 flex-shrink-0">
                  <span
                    v-if="
                      group.items.filter(i => i.status === 'sent').length > 0
                    "
                    class="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-n-teal-3 text-n-teal-11 border border-n-teal-6"
                  >
                    {{ group.items.filter(i => i.status === 'sent').length }}
                    {{ t('FOLLOW_UPS.STATUS.SENT') }}
                  </span>
                  <span
                    v-if="
                      group.items.filter(i => i.status === 'failed').length > 0
                    "
                    class="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-n-ruby-3 text-n-ruby-11 border border-n-ruby-6"
                  >
                    {{ group.items.filter(i => i.status === 'failed').length }}
                    {{ t('FOLLOW_UPS.STATUS.FAILED') }}
                  </span>
                  <span
                    class="i-lucide-chevron-down size-3.5 text-n-slate-10 transition-transform duration-200"
                    :class="{ 'rotate-180': isExpanded(group.label) }"
                  />
                </span>
              </button>
              <button
                v-if="group.conversation_id"
                type="button"
                class="mr-1 flex size-11 items-center justify-center rounded-md text-n-slate-10 hover:bg-n-alpha-2 hover:text-n-brand focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
                :aria-label="t('FOLLOW_UPS.LIST.VIEW_CONVERSATION')"
                @click="goToConversation(group.conversation_id)"
              >
                <span class="i-lucide-external-link size-3" />
              </button>
            </div>

            <!-- Items (collapsible) -->
            <div v-if="isExpanded(group.label)" class="divide-y divide-n-weak">
              <div
                v-for="message in group.items"
                :key="message.id"
                class="flex items-start gap-2.5 px-3 py-2.5 hover:bg-n-alpha-1 transition-colors"
              >
                <div
                  class="mt-0.5 w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
                  :class="
                    message.status === 'sent'
                      ? 'bg-n-teal-3'
                      : message.status === 'failed'
                        ? 'bg-n-ruby-3'
                        : 'bg-n-alpha-1'
                  "
                >
                  <span
                    class="size-3"
                    :class="[
                      getStatusIcon(message.status),
                      message.status === 'sent'
                        ? 'text-n-teal-11'
                        : message.status === 'failed'
                          ? 'text-n-ruby-11'
                          : 'text-n-slate-10',
                    ]"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <div class="flex items-center gap-2 mb-0.5">
                    <p
                      class="text-[11px] font-semibold text-n-slate-12 truncate"
                    >
                      {{ message.title || t('FOLLOW_UPS.LIST.NO_TITLE') }}
                    </p>
                    <span
                      class="flex-shrink-0 text-[9px] font-semibold px-1.5 py-0.5 rounded-full"
                      :class="getStatusClass(message.status)"
                    >
                      {{ getStatusLabel(message.status) }}
                    </span>
                  </div>
                  <p class="text-[11px] text-n-slate-11 line-clamp-1 mb-0.5">
                    {{ message.message }}
                  </p>
                  <p class="text-[10px] text-n-slate-10">
                    {{ formatDate(message.scheduled_at) }}
                  </p>
                </div>
                <NextButton
                  v-if="message.status === 'failed'"
                  :label="t('FOLLOW_UPS.LIST.RETRY')"
                  icon="i-lucide-rotate-cw"
                  variant="ghost"
                  color="slate"
                  size="xs"
                  :is-loading="isRetrying(message.id)"
                  :disabled="isRetrying(message.id)"
                  class="flex-shrink-0 self-center"
                  @click="handleRetry(message)"
                />
              </div>
            </div>

            <!-- Collapsed summary -->
            <button
              v-else
              type="button"
              class="min-h-11 w-full px-3 py-2 text-left text-[10px] text-n-slate-10 hover:text-n-slate-11 hover:bg-n-alpha-1 focus-visible:outline-2 focus-visible:outline-n-brand transition-colors"
              @click="toggleConversation(group.label)"
            >
              {{
                t('FOLLOW_UPS.VIEWS.SHOW_MESSAGES', {
                  count: group.items.length,
                })
              }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <ConfirmationModal
      ref="deleteConfirmationRef"
      :title="t('FOLLOW_UPS.DELETE.TITLE')"
      :description="t('FOLLOW_UPS.DELETE.CONFIRM')"
      :confirm-label="t('FOLLOW_UPS.DELETE.CONFIRM_BUTTON')"
      :cancel-label="t('FOLLOW_UPS.DELETE.CANCEL')"
    />

    <Modal
      v-model:show="showEditModal"
      size="small"
      :close-on-backdrop-click="!isUpdating"
      :close-on-escape="!isUpdating"
      :show-close-button="!isUpdating"
      aria-labelledby="follow-up-edit-title"
      @close="closeEditModal"
    >
      <div class="max-h-[85vh] overflow-y-auto p-5">
        <div class="mb-4 border-b border-n-weak pb-4">
          <h3
            id="follow-up-edit-title"
            data-modal-title
            class="text-sm font-semibold text-n-slate-12"
          >
            {{ t('FOLLOW_UPS.FORM.EDIT_TITLE') }}
          </h3>
        </div>

        <form class="space-y-4" @submit.prevent="handleEditSubmit">
          <div v-if="templates.length > 0">
            <label
              for="follow-up-template"
              class="mb-1.5 block text-sm font-medium text-n-slate-12"
            >
              {{ t('FOLLOW_UPS.FORM.TEMPLATE_LABEL') }}
            </label>
            <Select
              v-model="editForm.follow_up_template_id"
              input-id="follow-up-template"
              class="w-full"
              :options="templateOptions"
              :aria-label="t('FOLLOW_UPS.FORM.TEMPLATE_LABEL')"
              :disabled="isUpdating"
              @update:model-value="applyTemplate"
            />
          </div>

          <Input
            id="follow-up-title"
            v-model="editForm.title"
            type="text"
            :label="t('FOLLOW_UPS.FORM.TITLE_LABEL')"
            :placeholder="t('FOLLOW_UPS.FORM.TITLE_PLACEHOLDER')"
            custom-input-class="!h-11"
            :disabled="isUpdating"
          />

          <TextArea
            id="follow-up-message"
            v-model="editForm.message"
            :label="t('FOLLOW_UPS.FORM.MESSAGE_LABEL')"
            :placeholder="t('FOLLOW_UPS.FORM.MESSAGE_PLACEHOLDER')"
            custom-text-area-class="min-h-24"
            :disabled="isUpdating"
          />

          <Input
            id="follow-up-scheduled-at"
            v-model="editForm.scheduled_at"
            type="datetime-local"
            :label="t('FOLLOW_UPS.FORM.SCHEDULE_TIME_LABEL')"
            custom-input-class="!h-11"
            :disabled="isUpdating"
          />

          <div class="flex gap-2.5 pt-3 border-t border-n-weak">
            <NextButton
              type="button"
              variant="faded"
              color="slate"
              size="md"
              class="flex-1"
              :disabled="isUpdating"
              @click="closeEditModal"
            >
              {{ t('FOLLOW_UPS.FORM.CANCEL') }}
            </NextButton>
            <NextButton
              type="submit"
              variant="solid"
              color="blue"
              size="md"
              class="flex-1"
              :is-loading="isUpdating"
              :disabled="isUpdating"
            >
              {{ t('FOLLOW_UPS.FORM.UPDATE') }}
            </NextButton>
          </div>
        </form>
      </div>
    </Modal>
  </div>
</template>
